import React, { type ComponentType, type ReactNode, useCallback, type KeyboardEvent } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import InlineEdit, { type InlineEditProps } from '@atlaskit/inline-edit';
import Textarea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { ValidationError } from '../../types/validation.tsx';

export type Props = {
	/**
	 * Used to programmatically re-mount the InlineEdit component when changes are discarded in the UI. This is
	 * necessary to correctly reset the components internal edit state.
	 */
	issueTypeResetCount: number;
	value: string;
	messages: {
		label?: MessageDescriptor;
		descriptionPlaceholder: MessageDescriptor;
		descriptionPlaceholderIssueTerminologyRefreshM4?: MessageDescriptor;
		editDescriptionButtonLabel: MessageDescriptor;
	};
	onValidate: (arg1: string) => ValidationError | undefined;
	onChange: (arg1: string) => void;
};

export const DescriptionInlineInputTmp = (props: Props) => {
	const { onValidate, onChange, value, issueTypeResetCount, messages } = props;

	const { formatMessage } = useIntl();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const validateAndFormatMessage = (newValue: any) => {
		const error = onValidate(newValue);
		return error ? formatMessage(error.messageDescriptor, error.values) : '';
	};

	const descriptionPlaceholderMessage =
		messages.descriptionPlaceholderIssueTerminologyRefreshM4 &&
		fg('jira-issue-terminology-update-m4')
			? messages.descriptionPlaceholderIssueTerminologyRefreshM4
			: messages.descriptionPlaceholder;

	const renderReadView = useCallback(
		() =>
			// MC-961 Data attribute used for pollinator synthetic checks in
			// https://stash.atlassian.com/projects/JSRE/repos/pollinator-synthetic-checks
			// Checks: IssueTypesConfig
			value ? (
				<TextAreaReadWrapper data-pollinator-issue-type-description-read aria-hidden>
					{value}
				</TextAreaReadWrapper>
			) : (
				<TextAreaPlaceholder data-pollinator-issue-type-description-read>
					{formatMessage(descriptionPlaceholderMessage)}
				</TextAreaPlaceholder>
			),
		[value, formatMessage, descriptionPlaceholderMessage],
	);

	const renderEditView = useCallback<InlineEditProps<string>['editView']>(
		({ errorMessage, onChange: editViewOnChange, ...fieldProps }) => (
			// MC-961 Data attribute used for pollinator synthetic checks in
			// https://stash.atlassian.com/projects/JSRE/repos/pollinator-synthetic-checks
			// Checks: IssueTypesConfig
			<InlineDialog
				content={errorMessage}
				isOpen={fieldProps.isInvalid}
				placement="right"
				messageId="project-settings-apps-issue-type-page-product-features.common.ui.description-inline-input-tmp.inline-dialog"
				messageType="transactional"
			>
				<div data-pollinator-issue-type-description-edit>
					<Textarea
						{...fieldProps}
						autoFocus
						rows={1}
						onChange={(e) => editViewOnChange(e.target.value)}
					/>
				</div>
			</InlineDialog>
		),
		[],
	);

	const editButtonLabel = value
		? `${value}- ${formatMessage(messages.editDescriptionButtonLabel)}`
		: formatMessage(messages.editDescriptionButtonLabel);

	return (
		<InlineInputWrapper>
			<InlineEdit
				key={issueTypeResetCount}
				readView={renderReadView}
				editView={renderEditView}
				label={messages.label ? formatMessage(messages.label) : undefined}
				editButtonLabel={editButtonLabel}
				defaultValue={value}
				onConfirm={onChange}
				readViewFitContainerWidth
				validate={validateAndFormatMessage}
			/>
		</InlineInputWrapper>
	);
};

const minRows = 1;
const textAreaLineHeightFactor = 2.5;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextAreaReadWrapper = styled.div({
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * textAreaLineHeightFactor * minRows}px`,
	padding: `${token('space.075')} ${token('space.075')}`,
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TextAreaPlaceholder: ComponentType<{
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(TextAreaReadWrapper)({
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BlockWrapper = styled.div<{
	children?: ReactNode;
}>({
	/* Ensure block elements in the header have correct vertical spacing */
	marginBottom: token('space.200'),
});

// @ts-expect-error - TS2322 - Type 'StyledComponentClass<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { children?: ReactNode; }, any, ClassAttributes<...> & ... 1 more ... & { ...; }>' is not assignable to type 'ComponentType<{ children?: ReactNode; onKeyDown?: ((arg1: KeyboardEvent<HTMLElement>) => void) | undefined; }>'.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InlineInputWrapper: ComponentType<{
	children?: ReactNode;
	onKeyDown?: (arg1: KeyboardEvent<HTMLElement>) => void;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(BlockWrapper)({
	/* Add negative horizontal margin equal to the input padding to ensure the input text is left aligned */
	marginLeft: token('space.negative.100'),
	marginRight: token('space.negative.100'),
	/* Offset the above negative margin to re-align the input label */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	label: {
		margin: `0 ${token('space.100')}`,
	},
	/* Inline edit has a fixed margin top of 8px that we need to override */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		marginTop: 0,
	},
});
